import { Dispatch, SetStateAction, Fragment } from "react";
import classNames from "classnames";
import dayjs from "dayjs";

import {
  translate,
  FetchAnalyticsStudentProjectResponse,
} from "@ascendnow/shared";

import Modal from "../AscModal/AscModal";
import styles from "./AscProjectViewModal.module.scss";
import AscLoading from "../AscLoading/AscLoading";
import AscTag from "../AscTag/AscTag";
import AscImage from "../AscImage/AscImage";

export interface AscProjectViewModalProps {
  toggleModal: boolean;
  setToggleModal: Dispatch<SetStateAction<boolean>>;
  project: FetchAnalyticsStudentProjectResponse;
  isDownloading?: boolean;
  className?: string;
}

export default function AscProjectViewModal({
  toggleModal,
  setToggleModal,
  project,
  isDownloading = false,
  className,
}: AscProjectViewModalProps) {
  return (
    <Modal
      header={
        !isDownloading && (
          <div className={styles.projectHeader}>
            <span className={styles.projectTitle}>{project?.projectName}</span>
            <div className={styles.projectTagList}>
              {project?.skills?.map((skill) => (
                <AscTag
                  key={skill.id}
                  label={skill?.name}
                  className={styles.projectTag}
                />
              ))}
            </div>
          </div>
        )
      }
      visible={toggleModal}
      setVisible={setToggleModal}
      withBackground={false}
      modalClassName={styles.projectViewModal}
      innerContentClassName={styles.projectViewModalContent}
      headerClassName={styles.projectViewModalHeader}
    >
      <AscLoading
        loading={isDownloading}
        type="spinner"
        className={styles.projectViewModalSpinnerContainer}
      >
        <div
          data-testid="asc-project-view-modal"
          className={classNames(styles.ground, className)}
          tabIndex={0}
        >
          <div className={styles.projectPositionWrapper}>
            <span className={styles.projectPosition}>{project?.position}</span>{" "}
            <span className={styles.projectDate}>
              {dayjs(project?.startDate).format("MMM YYYY")}{" "}-{" "}
              {project?.endDate
                ? dayjs(project?.endDate).format("MMM YYYY")
                : translate("present")}
            </span>
          </div>
          {project?.projectImages && project?.projectImages?.length > 0 && (
            <div className={styles.projectImageList}>
              {project?.projectImages?.map((image) => (
                <AscImage
                  key={image.id}
                  src={image?.location}
                  className={styles.projectImage}
                />
              ))}
            </div>
          )}
          <p className={styles.projectDescription}>
            {project?.projectDescription}
          </p>
          {project?.results && project?.results?.length > 0 && (
            <div className={styles.projectAchievementList}>
              {project?.results?.map((result) => (
                <div key={result?.id} className={styles.projectAchievement}>
                  <span
                    className={styles.projectAchievementIcon}
                    style={{ backgroundImage: `url(${result?.location})` }}
                  />
                  <span className={styles.projectAchievementTitle}>
                    {result?.description}
                  </span>
                </div>
              ))}
            </div>
          )}
          {project?.taskDescriptions &&
            project?.taskDescriptions?.length > 0 && (
              <div className={styles.projectTaskListWrapper}>
                <span className={styles.projectTaskListTitle}>
                  {translate("tasks")}
                </span>
                <ul className={styles.projectTaskList}>
                  {project?.taskDescriptions?.map((task) => (
                    <li key={task?.id} className={styles.projectTask}>
                      {task?.description}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          {project?.challenges && project?.challenges?.length > 0 && (
            <div className={styles.projectChallengeList}>
              {project?.challenges?.map((challenge) => (
                <div key={challenge?.id} className={styles.projectChallenge}>
                  <span className={styles.projectChallengeTitle}>
                    {challenge?.name}
                  </span>
                  <div className={styles.projectChallengeAchievement}>
                    {challenge?.description}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </AscLoading>
    </Modal>
  );
}
